import React, { useState, useEffect } from "react";
import { Button, Dropdown, Form, Icon, Loader } from "semantic-ui-react";
import agent, { PaginateOptions } from "../../agent";
import {
    DoctorTitleCase,
    GeneralTitleCase,
    getDoctorId,
    getDoctorTitle,
    getGeneralTitle,
    getPatientTitle,
    PatientTitleCase,
} from "../../utils/common";
import { hasRole, UserRole } from "../../helpers/UserRole";
import MaskedDatePicker from "../general/MaskedDatePicker";
import { handleDatePickerKeyDown } from "../../helpers/CalendarEventsRepeater";
import { handleOnlyDigits, handleOnlyLettersAndSpecialChars } from "../../consts/onKeyDownValidation";
import { textLabels } from "../../consts/textLabels";
import { getPatientDeptOptions } from "../../consts/PatientConsts";
import { getEducationFormsOptions, getFundingSourceOptions } from "../../consts/tambsuConsts";
import {Dept, Doctor, SpecialGroup} from "../dashboard";
import {firstLetterToUpperCase} from "./PatientsList";

export interface PatientFilters {
    searchValue?: string;
    diagnosisFilter?: string;
    telFilter?: string;
    instituteFilter?: string;
    specializationFilter?: string;
    courseFilter?: string;
    structuralDivisionFilter?: string;
    activatedFilter?: boolean;
    activatedDateFromFilter?: string;
    activatedDateToFilter?: string;
    registerDateFromFilter?: string;
    registerDateToFilter?: string;
    deptId?: string;
    doctorId?: string;
    specialGroupsIdsFilter?: string[];
    patientDeptsIdsFilter?: string[];
    typeFilter?: "all" | "notLargeFamily" | "isLargeFamily";
    educationFormFilter?: string;
    fundingSourceFilter?: string;
}

export const initialFilterState: PatientFilters = {
    searchValue: "",
    activatedFilter: undefined,
    activatedDateFromFilter: undefined,
    activatedDateToFilter: undefined,
    registerDateFromFilter: undefined,
    registerDateToFilter: undefined,
    diagnosisFilter: undefined,
    telFilter: undefined,
    deptId: undefined,
    doctorId: undefined,
    specialGroupsIdsFilter: undefined,
    patientDeptsIdsFilter: undefined,
    typeFilter: "all",
    instituteFilter: undefined,
    specializationFilter: undefined,
    courseFilter: undefined,
    educationFormFilter: undefined,
    fundingSourceFilter: undefined,
    structuralDivisionFilter: undefined,
}

interface FilterPanelProps {
    initialFilters: PatientFilters;
    onSearch: (filters: PatientFilters) => void;
    onReset: () => void;
    selectedCount?: number;
    onDeselectHidden?: () => void;
    onDeselectAll?: () => void;
    page?: number;
}

const FilterPanel: React.FC<FilterPanelProps> = ({
     initialFilters,
     onSearch,
     onReset,
     selectedCount = 0,
     onDeselectHidden,
     onDeselectAll,
     page,
 }) => {
    const [depts, setDepts] = useState<Dept[]>([]);
    const [doctors, setDoctors] = useState<Doctor[]>([]);
    const [specialgroups, setSpecialGroups] = useState<SpecialGroup[]>([]);
    const [loadingRefs, setLoadingRefs] = useState(true);

    const [filters, setFilters] = useState<PatientFilters>(initialFilters);
    const [showAllFilters, setShowAllFilters] = useState(false);

    useEffect(() => {
        setFilters(initialFilters);
    }, [initialFilters, page]);

    // Загрузка справочников
    useEffect(() => {
        const loadReferenceData = async () => {
            setLoadingRefs(true);
            try {
                const paginateOptions: PaginateOptions = {
                    limit: 10000,
                    page: 1,
                    search: "",
                    sort: "surname"
                };

                const [
                    deptsRes,
                    doctorsRes,
                    specialGroupsRes
                ] = await Promise.all([
                    agent.DeptProvider.getList(paginateOptions),
                    agent.DoctorProvider.getList(paginateOptions),
                    agent.SpecialGroupProvider.getList(),
                ]);

                setDepts(deptsRes.docs || []);
                setDoctors(doctorsRes.docs || []);
                setSpecialGroups(specialGroupsRes.docs || []);
            } catch (error) {
                console.error("Ошибка загрузки справочников:", error);
            } finally {
                setLoadingRefs(false);
            }
        };

        loadReferenceData();
    }, []);

    // Обработчик для изменения любого фильтра
    const handleFilterChange = (field: keyof PatientFilters, value: any) => {
        const normalizedValue = value === "" ? undefined : value;
        setFilters(prev => ({ ...prev, [field]: normalizedValue }));
    };

    // Обработчик для массивных фильтров (множественный выбор)
    const handleArrayFilterChange = (field: keyof PatientFilters, value: string[]) => {
        const normalizedValue = value.length > 0 ? value : undefined;
        setFilters(prev => ({ ...prev, [field]: normalizedValue }));
    };

    // Парсинг даты из строки в часовом поясе
    const parseLocalDate = (dateStr?: string | Date | null): Date | null => {
        if (!dateStr) return null;
        // Если уже Date объект, вернуть его
        if (dateStr instanceof Date) return dateStr;
        // Если не строка, вернуть null
        if (typeof dateStr !== 'string') return null;
        const [year, month, day] = dateStr.split('-').map(Number);
        if (isNaN(year) || isNaN(month) || isNaN(day)) return null;
        return new Date(year, month - 1, day);
    };


    // Обработчик для дат
    const handleDateChange = (field: keyof PatientFilters, date?: Date | null) => {
        if (!date) {
            setFilters(prev => ({ ...prev, [field]: undefined }));
            return;
        }
        // Формируем строку даты в часовом поясе
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const normalizedValue = `${year}-${month}-${day}`;
        setFilters(prev => ({ ...prev, [field]: normalizedValue }));
    };

    // Обработчик нажатия клавиши Enter
    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    // Обработчик поиска
    const handleSearch = () => {
        onSearch(filters);
    };

    // Обработчик сброса фильтров
    const handleReset = () => {
        setFilters({});
        onReset();
    };

    const toArray = (value: string | string[] | undefined): string[] | undefined => {
        if (!value) return undefined;
        return Array.isArray(value) ? value : [value];
    };

    // Нормализация значений для Dropdown
    const specialGroupsFilterValue = toArray(filters.specialGroupsIdsFilter) || [];
    const patientDeptsFilterValue = toArray(filters.patientDeptsIdsFilter) || [];

    const activatedOptions = [
        { key: "true", text: "Да", value: "true" },
        { key: "false", text: "Нет", value: "false" },
    ];

    // Если идет загрузка справочников
    if (loadingRefs) {
        return (
            <div style={{ padding: "20px", textAlign: "center" }}>
                <Loader active inline="centered">Загрузка фильтров...</Loader>
            </div>
        );
    }

    return (
        <Form>
            <Form.Group>
                <Form.Input
                    label="ФИО"
                    width="4"
                    iconPosition="left"
                    icon={<Icon name="search" />}
                    value={filters.searchValue || ""}
                    onChange={(e) => handleFilterChange('searchValue', e.target.value)}
                    onKeyDown={(e: any) => {
                        handleOnlyLettersAndSpecialChars(e);
                        handleKeyPress(e);
                    }}
                />
                <Form.Field>
                    <label>{getGeneralTitle(GeneralTitleCase.diagnostab)}</label>
                    <Form.Input
                        iconPosition="left"
                        icon={<Icon name="search" />}
                        value={filters.diagnosisFilter || ""}
                        onChange={(e) => handleFilterChange('diagnosisFilter', e.target.value)}
                        onKeyDown={handleKeyPress}
                    />
                </Form.Field>
                {!hasRole(UserRole.DOCTOR) && (
                    <Form.Field width="3">
                        <label>{firstLetterToUpperCase(getGeneralTitle(GeneralTitleCase.departmantim))}</label>
                        <Dropdown
                            selection
                            search
                            clearable
                            disabled={!!filters.doctorId}
                            value={filters.deptId || ""}
                            onChange={(_, data) => handleFilterChange('deptId', data.value as string)}
                            options={depts.map((d) => ({ key: d._id, text: d.name, value: d._id }))}
                            noResultsMessage={textLabels.noResultsMessage}
                        />
                    </Form.Field>
                )}
                <Form.Field width="3">
                    <label>{firstLetterToUpperCase(getGeneralTitle(GeneralTitleCase.departmantim))} {getPatientTitle(PatientTitleCase.a)}</label>
                    <Dropdown
                        selection
                        multiple
                        search
                        clearable
                        value={patientDeptsFilterValue}
                        onChange={(_, data) =>
                            handleArrayFilterChange('patientDeptsIdsFilter', (data.value as string[]) || [])
                        }
                        options={
                            !hasRole(UserRole.DOCTOR)
                                ? depts.map((d) => ({ key: d._id, text: d.name, value: d._id }))
                                : getPatientDeptOptions(doctors, filters.doctorId || "", depts)
                        }
                        noResultsMessage={textLabels.noResultsMessage}
                    />
                </Form.Field>
            </Form.Group>

            <Form.Group style={{ alignItems: 'flex-end' }}>
                <Form.Field width="4">
                    <label>{firstLetterToUpperCase(getDoctorTitle(DoctorTitleCase.im))}</label>
                    <Dropdown
                        selection
                        search
                        disabled={!!getDoctorId()}
                        clearable
                        value={filters.doctorId || ""}
                        onChange={(_, data) => {
                            const newDoctorId = data.value as string;
                            setFilters(prev => ({
                                ...prev,
                                doctorId: newDoctorId === "" ? undefined : newDoctorId,
                                ...(newDoctorId && newDoctorId !== "" ? { deptId: undefined } : {})
                            }));
                        }}
                        options={doctors.map((d) => ({
                            key: d._id,
                            text: `${d.surname} ${d.name} ${d.middlename}`,
                            value: d._id
                        }))}
                        noResultsMessage={textLabels.noResultsMessage}
                    />
                </Form.Field>
                <Form.Field width="4">
                    <label>{getGeneralTitle(GeneralTitleCase.specialgrouptab)}</label>
                    <Dropdown
                        selection
                        multiple
                        search
                        clearable
                        value={specialGroupsFilterValue}
                        onChange={(_, data) =>
                            handleArrayFilterChange('specialGroupsIdsFilter', (data.value as string[]) || [])
                        }
                        options={specialgroups
                            .sort((a, b) => a.name.localeCompare(b.name))
                            .map((sg) => ({ key: sg._id, text: sg.name, value: sg._id }))}
                        noResultsMessage={textLabels.noResultsMessage}
                    />
                </Form.Field>
                <Form.Button
                    basic
                    type="button"
                    content={
                        <>
                            Все фильтры {showAllFilters ? <Icon name="chevron up" /> : <Icon name="chevron down" />}
                        </>
                    }
                    color={showAllFilters ? "grey" : "blue"}
                    onClick={() => setShowAllFilters(!showAllFilters)}
                />
                <Form.Button primary content="Поиск" onClick={handleSearch} />
                <Button basic color="orange" content="Сбросить" onClick={handleReset} />
                {selectedCount > 0 && onDeselectHidden && (
                    <Button basic color="orange" content="Снять отметки со скрытых" onClick={onDeselectHidden} />
                )}
                {selectedCount > 0 && onDeselectAll && (
                    <Button basic color="orange" content="Снять все отметки" onClick={onDeselectAll} />
                )}
            </Form.Group>

            {showAllFilters && (
                <>
                    <Form.Group>
                        <Form.Field width="3">
                            <label>Активирован</label>
                            <Dropdown
                                clearable
                                options={activatedOptions}
                                selection
                                value={filters.activatedFilter === undefined ? "" : filters.activatedFilter.toString()}
                                onChange={(_, data) => {
                                    if (data.value === "" || data.value === undefined) {
                                        handleFilterChange('activatedFilter', undefined);
                                    } else {
                                        handleFilterChange('activatedFilter', data.value === "true");
                                    }
                                }}
                            />
                        </Form.Field>
                        <Form.Field width={2}>
                            <label>Дата регистрации с</label>
                            <MaskedDatePicker
                                selected={parseLocalDate(filters.registerDateFromFilter)}
                                onChange={(date?: Date | null) => handleDateChange('registerDateFromFilter', date)}
                                onKeyDown={handleDatePickerKeyDown}
                            />
                        </Form.Field>
                        <Form.Field width={2}>
                            <label>Дата регистрации до</label>
                            <MaskedDatePicker
                                selected={parseLocalDate(filters.registerDateToFilter)}
                                onChange={(date?: Date | null) => handleDateChange('registerDateToFilter', date)}
                                onKeyDown={handleDatePickerKeyDown}
                                minDate={filters.registerDateFromFilter ? new Date(filters.registerDateFromFilter) : undefined}
                                maxDate={new Date()}
                            />
                        </Form.Field>
                        <Form.Field width={2}>
                            <label>Дата активации с</label>
                            <MaskedDatePicker
                                selected={parseLocalDate(filters.activatedDateFromFilter)}
                                onChange={(date?: Date | null) => handleDateChange('activatedDateFromFilter', date)}
                                onKeyDown={handleDatePickerKeyDown}
                            />
                        </Form.Field>
                        <Form.Field width={2}>
                            <label>Дата активации до</label>
                            <MaskedDatePicker
                                selected={parseLocalDate(filters.activatedDateToFilter)}
                                onChange={(date?: Date | null) => handleDateChange('activatedDateToFilter', date)}
                                onKeyDown={handleDatePickerKeyDown}
                                minDate={filters.activatedDateFromFilter ? new Date(filters.activatedDateFromFilter) : undefined}
                                maxDate={new Date()}
                            />
                        </Form.Field>
                    </Form.Group>
                    <Form.Group>
                        <Form.Field width="3">
                            <label>Телефон</label>
                            <Form.Input
                                iconPosition="left"
                                icon={<Icon name="search" />}
                                value={filters.telFilter || ""}
                                onChange={(e) => {
                                    const val = e.target.value.trim();
                                    handleFilterChange('telFilter', val ? val.replace(/[^0-9]/g, "") : "");
                                }}
                                onKeyDown={(e: any) => {
                                    handleOnlyDigits(e);
                                    handleKeyPress(e);
                                    if (e.key === "Enter") e.preventDefault();
                                }}
                            />
                        </Form.Field>
                    </Form.Group>
                    {window.config?.tambsu && (
                        <Form.Group widths="equal">
                            <Form.Input
                                label="Институт"
                                iconPosition="left"
                                icon={<Icon name="search" />}
                                value={filters.instituteFilter || ""}
                                onChange={(e) => handleFilterChange('instituteFilter', e.target.value)}
                                onKeyDown={handleKeyPress}
                            />
                            <Form.Input
                                label="Специальность"
                                iconPosition="left"
                                icon={<Icon name="search" />}
                                value={filters.specializationFilter || ""}
                                onChange={(e) => handleFilterChange('specializationFilter', e.target.value)}
                                onKeyDown={handleKeyPress}
                            />
                            <Form.Input
                                label="Курс"
                                iconPosition="left"
                                icon={<Icon name="search" />}
                                value={filters.courseFilter || ""}
                                onChange={(e) => handleFilterChange('courseFilter', e.target.value)}
                                onKeyDown={handleKeyPress}
                            />
                            <Form.Field>
                                <label>Форма обучения</label>
                                <Dropdown
                                    selection
                                    search
                                    clearable
                                    value={filters.educationFormFilter || ""}
                                    onChange={(_, data) => handleFilterChange('educationFormFilter', data.value as string)}
                                    options={getEducationFormsOptions()}
                                    noResultsMessage={textLabels.noResultsMessage}
                                />
                            </Form.Field>
                            <Form.Field>
                                <label>Источник финансирования</label>
                                <Dropdown
                                    selection
                                    search
                                    clearable
                                    value={filters.fundingSourceFilter || ""}
                                    onChange={(_, data) => handleFilterChange('fundingSourceFilter', data.value as string)}
                                    options={getFundingSourceOptions()}
                                    noResultsMessage={textLabels.noResultsMessage}
                                />
                            </Form.Field>
                            <Form.Input
                                label="Структурное подразделение"
                                iconPosition="left"
                                icon={<Icon name="search" />}
                                value={filters.structuralDivisionFilter || ""}
                                onChange={(e) => handleFilterChange('structuralDivisionFilter', e.target.value)}
                                onKeyDown={handleKeyPress}
                            />
                        </Form.Group>
                    )}
                </>
            )}
        </Form>
    );
};

export default FilterPanel;